import React from 'react';

import {PageHeadline} from 'src/components/page-headline/page-headline';
import {RemoveEvent} from 'src/components/remove-event/remove-event';
import {NO_DATA_AVAILABLE} from 'src/constants';
import {useUserRoles} from 'src/hooks/useUserRoles';
import {EventExcelDownload} from 'src/modules/event/download/event-excel-download';
import {
    EventTriggerAttachmentController,
} from 'src/modules/event/event-trigger-attachment/event-trigger-attachment.controller';
import {EventSubscriptionDropdown} from 'src/modules/event/subscription/event-subscription.dropdown';
import {Attributes} from 'src/modules/event/views/attributes/attributes';
import {ChangeLog} from 'src/modules/event/views/change-log/change-log';
import {CopierField} from 'src/modules/event/views/copier-field/copier-field';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {EventDetailInfo} from 'src/openapi/models/EventDetailInfo';
import {EventType} from 'src/openapi/models/EventType';
import {Role} from 'src/openapi/models/Role';
import {User} from 'src/openapi/models/User';
import {capitalize} from 'src/utils/capitalize';

import Button from '@pb/kit-component-button';
import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import {Tooltip} from '@pb/kit-component-popups';
import {
    HorizontalPositionElement,
    PushNotification,
    VerticalPositionElement,
} from '@pb/kit-component-push-notification';
import {Scrollable} from '@pb/kit-component-scrollable';
import {formatDate} from '@pb/kit-formatters';

import {eventDeprecated, eventTrigger, eventTriggerText} from './event.module.styl';

type Props = {
    eventCode: string,
    eventDetailInfo: EventDetailInfo,
    changeRequests: Array<ChangeRequest>,
    updateHandler: () => void,
    seeChangeRequestHandler: () => void,
    openChangeRequest: boolean,
    user: User | null,
};

const NOTIFICATION_CONFIG = {
    horizontal: HorizontalPositionElement.RIGHT,
    vertical: VerticalPositionElement.BOTTOM,
};

const UPDATE_TOOLTIP_CAPTION =
    'Event cannot be updated because it already has an existing change request in process.';

export const EventView = (props: Props) => {
    const {
        openChangeRequest,
        eventCode,
        changeRequests,
        eventDetailInfo,
        updateHandler,
        seeChangeRequestHandler,
        user,
    } = props;
    const userRoles = useUserRoles();

    const updateButton = (
        <Button
            onClick={updateHandler}
            disabled={openChangeRequest}
            variant="Type.PRIMARY">
            Update
        </Button>
    );

    return (
        <>
            <PageHeadline caption={`Event Code | ${eventCode}`} captionToCopy={eventCode}>
                <Group>
                    <EventExcelDownload
                        eventDetailInfo={eventDetailInfo}
                        changeRequests={changeRequests}
                        user={user}
                    />
                    {userRoles.includes(Role.ROLE_UDEDT_ADMIN) && (
                        <PushNotification anchorOrigin={NOTIFICATION_CONFIG}>
                            <RemoveEvent eventDetailInfo={eventDetailInfo}/>
                        </PushNotification>
                    )}
                    {openChangeRequest ? (
                        <Group>
                            <Tooltip anchor={({anchorRef}) => (
                                <div ref={anchorRef}>{updateButton}</div>
                            )}>
                                <span>{UPDATE_TOOLTIP_CAPTION}</span>
                            </Tooltip>
                            <Button onClick={seeChangeRequestHandler} variant="Type.SECONDARY">
                                View Change Request
                            </Button>
                        </Group>
                    ) : updateButton}
                    <EventSubscriptionDropdown eventId={eventDetailInfo.id}/>
                </Group>
            </PageHeadline>
            <Group direction="v" gap="xl">
                <Item style={{width: '100%'}}>
                    <Group justify="space-between">
                        <Item style={{width: '23%'}}>
                            <Group direction="v" alignContent="start" justify="start" gap="xl">
                                <Group direction="v" gap="sm">
                                    <Group>
                                        <strong>Event Type:</strong>
                                        {eventDetailInfo.type}
                                    </Group>
                                    <Group>
                                        <strong>Event Source:</strong>
                                        {eventDetailInfo.source}
                                    </Group>
                                    <Group>
                                        <strong>Families:</strong>
                                        <EllipsisTextHint>
                                            {eventDetailInfo?.families ? eventDetailInfo.families.join(', ') : ''}
                                        </EllipsisTextHint>
                                    </Group>
                                </Group>
                                <Group direction="v" gap="m">
                                    {eventDetailInfo.type === EventType.USER_EVENT ? (
                                        <Group>
                                            <strong>Reported:</strong>
                                            {capitalize(eventDetailInfo.reported.toString())}
                                        </Group>
                                    ) : null}
                                    {eventDetailInfo.type === EventType.USER_EVENT ? (
                                        <Group>
                                            <strong>Mixpanel Decorated:</strong>
                                            {capitalize(eventDetailInfo.decorated.toString())}
                                        </Group>
                                    ) : null}
                                    <Group className={eventDetailInfo.deprecated ? eventDeprecated : ''}>
                                        <strong>Deprecated:</strong>
                                        {capitalize(eventDetailInfo.deprecated.toString())}
                                    </Group>
                                </Group>
                                <Group direction="v" gap="sm">
                                    <Group>
                                        <strong>First Usage Date:</strong>
                                        {formatDate(eventDetailInfo.firstUsageDate)}
                                    </Group>
                                    <Group>
                                        <strong>Last Usage Date:</strong>
                                        {formatDate(eventDetailInfo.lastUsageDate)}
                                    </Group>
                                </Group>
                            </Group>
                        </Item>
                        <Item style={{width: '43%'}}>
                            <Group direction="v" gap="l">
                                <Item style={{width: '100%'}}>
                                    <Group gap="l" justify="start">
                                        <Item shrink={0}>
                                            <strong>Event Description:</strong>
                                        </Item>
                                        <Item grow={1} style={{minWidth: 0}}>
                                            {eventDetailInfo?.description ? (
                                                <CopierField textToCopy={eventDetailInfo.description}/>
                                            ) : null}
                                        </Item>
                                    </Group>
                                </Item>
                                <Item style={{width: '100%'}}>
                                    <Group direction="v" gap="l">
                                        <Group direction="v" gap="s">
                                            <strong>Trigger:</strong>
                                            <Item className={eventTrigger}>
                                                <Scrollable>
                                                    <span className={eventTriggerText}>
                                                        {eventDetailInfo?.trigger ?? NO_DATA_AVAILABLE}
                                                    </span>
                                                </Scrollable>
                                            </Item>
                                        </Group>
                                        <EventTriggerAttachmentController eventDetailInfo={eventDetailInfo}/>
                                    </Group>
                                </Item>
                            </Group>
                        </Item>
                        <Item style={{width: '34%'}}>
                            <ChangeLog changeRequests={changeRequests}/>
                        </Item>
                    </Group>
                </Item>
                <Attributes eventDetailInfo={eventDetailInfo}/>
            </Group>
        </>
    );
};
